"use client"

import Link from "next/link"
import Image from "next/image"
import { useTranslations } from "next-intl"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { useLivePrices } from "@/lib/use-live-prices"
import { blobMediaUrl } from "@/lib/blob-url"
import { goKeys as k } from "@/i18n/go-keys"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { CheckCircleIcon } from "@/components/ui/icons"
import { BiomarkerDiseaseExplorer } from "@/components/ui/biomarker-disease-explorer"
import { baseBloodTest } from "@/lib/base-blood-test"
import { ReportMockupIpad } from "@/components/ui/report-mockup-ipad"
import { BloodTrackingComparison } from "@/components/ui/blood-tracking-comparison"
import { ImmuneStepsTimeline } from "@/components/ui/immune-steps-timeline"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { STATS, MEASURE_CARDS, PROCESS } from "./female-hormones-data"
import {
  SECTION,
  CtaReassurance,
  useFullCheckupHref,
} from "./female-hormones-components"

/* ── Coverage explorer — live CMS-backed biomarker browser. This section owns
   the header; `hideHeader` keeps the explorer aligned (AHD-8117 / the
   metabolism pattern). The base panel is the market's own blood product
   (`baseBloodTest`) and drives the counts; the add-on surfaces alongside. ── */
export function FemaleHormonesCoverage() {
  const t = useTranslations("go")
  const lp = useLocalePath()
  const { code: country } = useCountry()
  return (
    <section className="bg-neutral-50 py-16 md:py-24">
      <div className={SECTION}>
        <div className="mx-auto flex max-w-[720px] flex-col items-center gap-3 text-center md:gap-4">
          <p className="type-label-md text-neutral-600 uppercase">
            {t(k.femaleHormonesCoverageEyebrow)}
          </p>
          <h2 className="type-display-md text-neutral-900">
            {t(k.femaleHormonesCoverageHeading)}
          </h2>
          <p className="type-body-lg max-w-[520px] text-neutral-600">
            {t(k.femaleHormonesCoverageBody)}
          </p>
        </div>
        <div className="mt-12">
          <BiomarkerDiseaseExplorer
            packages={[
              baseBloodTest(country).biomarkerPackage,
              "addon-hormones-f",
            ]}
            tabsAllowed={["biomarkers"]}
            defaultTab="biomarkers"
            hideHeader
            emphasizeAddonCategory
            biomarkersTab={{
              cta: {
                label: t(k.bloodtestBiomarkersCta),
                href: lp("/biomarkers"),
                variant: "text",
              },
            }}
          />
        </div>
        <div className="mt-10 flex justify-center">
          <BookingCta
            productId={[PRODUCT_IDS.bloodTest, PRODUCT_IDS.hormoneWomen]}
            step="addons"
            trackingLabel="female_hormones_coverage_cta"
            trackingLocation="go/female-hormones"
            className="block w-full sm:inline-block sm:w-auto"
          >
            <Button
              size="lg"
              variant="primary"
              className="group w-full sm:w-auto"
              iconRight={
                <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
              }
            >
              {t(k.femaleHormonesHeroCta1)}
            </Button>
          </BookingCta>
        </div>
      </div>
    </section>
  )
}

/* ── How it works — scroll-linked stepper. ── */
export function FemaleHormonesProcess({
  bloodDrawCount,
  showImaging,
}: {
  /** Live count of active blood-draw labs, interpolated into step 1. */
  bloodDrawCount: number
  /** Blood-only markets drop the optional pelvic-MRI visit. */
  showImaging: boolean
}) {
  const t = useTranslations("go")
  // Step 1 advertises the live number of blood-draw labs from the CMS via the
  // non-ICU `[[count]]` sentinel (a real `{count}` placeholder would force the
  // shared "go" namespace to require interpolation args on every generic t()
  // call), interpolated here. Dropping the imaging step renumbers the rest for
  // free — the timeline numbers steps by position.
  const steps = PROCESS.filter((s) => showImaging || !("imaging" in s)).map(
    (s, i) =>
      i === 0
        ? {
            ...s,
            desc: t(k.femaleHormonesProcessStep1Desc).replace(
              "[[count]]",
              String(bloodDrawCount)
            ),
          }
        : s
  )
  return (
    <ImmuneStepsTimeline
      label={t(k.femaleHormonesProcessLabel)}
      headingKey={k.femaleHormonesProcessHeading}
      bodyKey={k.femaleHormonesProcessBody}
      stepWord={t(k.femaleHormonesProcessStepWord)}
      steps={steps}
      namespace="go"
      cta={
        <div className="flex flex-col items-center gap-6">
          <BookingCta
            productId={[PRODUCT_IDS.bloodTest, PRODUCT_IDS.hormoneWomen]}
            step="addons"
            trackingLabel="female_hormones_how_it_works_cta"
            trackingLocation="go/female-hormones"
            className="block w-full sm:inline-block sm:w-auto"
          >
            <Button
              size="lg"
              variant="primary"
              className="group w-full sm:w-auto"
              iconRight={
                <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
              }
            >
              {t(k.femaleHormonesHeroCta1)}
            </Button>
          </BookingCta>
          <CtaReassurance />
        </div>
      }
    />
  )
}

/* ── Your report — the tangibility beat (metabolism layout: copy beside the
   iPad report mockup bleeding off the panel's right edge). ── */
export function FemaleHormonesReport() {
  const t = useTranslations("go")
  const lp = useLocalePath()
  return (
    <section className="pt-2 pb-6 md:pt-4 md:pb-8">
      <div className={SECTION}>
        <div className="relative overflow-hidden rounded-[32px] bg-white">
          <div className="grid items-stretch md:grid-cols-[0.82fr_1.18fr]">
            <div className="flex flex-col justify-center px-8 py-12 md:px-14 md:py-20 lg:px-16">
              <p className="type-label-md text-neutral-600 uppercase">
                {t(k.femaleHormonesReportEyebrow)}
              </p>
              <h2 className="type-display-md mt-3 max-w-[420px] text-neutral-900">
                {t(k.femaleHormonesReportHeading)}
              </h2>
              <p className="type-body-lg mt-5 max-w-[460px] text-neutral-600">
                {t(k.femaleHormonesReportBody)}
              </p>
              <div className="mt-10">
                <Link
                  href={lp("/report")}
                  className="block w-full sm:inline-block sm:w-auto"
                >
                  <Button variant="tonal" className="w-full sm:w-auto">
                    {t(k.femaleHormonesReportCta)}
                  </Button>
                </Link>
              </div>
            </div>
            {/* right — iPad report mockup, cropped flush to the panel edges */}
            <div className="relative min-h-[440px] md:min-h-[600px]">
              <div className="absolute top-10 left-0 w-[135%] md:top-14">
                <ReportMockupIpad />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── What we measure — the single measure block, in the shared metabolism/
   immunity "health-risks card" design (photo tile + organ icon · title ·
   description · count eyebrow · marker chips). Four groups: three add-on hormone
   panels + the base-test thyroid/metabolic markers. ── */
function MeasureMarkerCard({
  card,
  t,
}: {
  card: (typeof MEASURE_CARDS)[number]
  t: ReturnType<typeof useTranslations>
}) {
  const Icon = card.Icon
  return (
    <div className="flex flex-col items-center gap-6 rounded-[24px] bg-neutral-50 px-6 pt-10 pb-8 text-center">
      <span className="relative flex h-36 w-36 items-center justify-center overflow-hidden rounded-[52px] text-white">
        <Image
          src={card.tileImage}
          alt=""
          fill
          sizes="144px"
          className="object-cover"
        />
        <span
          aria-hidden
          className="absolute inset-0 bg-gradient-to-br from-white/25 via-transparent to-black/30"
        />
        <Icon className={`relative z-10 ${card.iconClassName}`} />
      </span>
      {/* reserve 2 lines so 1- and 2-line titles keep the description on a
          consistent baseline across cards */}
      <h3 className="type-headline-sm flex min-h-[60px] max-w-[200px] items-start justify-center text-neutral-900">
        {t(card.titleKey)}
      </h3>
      <p className="type-body-lg text-neutral-600">{t(card.bodyKey)}</p>
      <div className="mt-auto flex flex-col items-center gap-4 pt-2">
        <p className="type-label-md text-neutral-400 uppercase">
          {t(card.countKey)}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          {t(card.markersKey)
            .split("·")
            .map((marker) => (
              <span
                key={marker}
                className="type-label-md rounded-full border border-neutral-200 px-2 py-1 text-neutral-900 normal-case"
              >
                {marker.trim()}
              </span>
            ))}
        </div>
      </div>
    </div>
  )
}

export function FemaleHormonesMeasure() {
  const t = useTranslations("go")
  return (
    <section
      id="whats-measured"
      className="scroll-mt-24 bg-white py-16 pb-24 md:py-24 md:pb-28"
    >
      <div className={SECTION}>
        <div className="mx-auto flex max-w-[677px] flex-col items-center gap-4 text-center">
          <p className="type-label-md text-neutral-600 uppercase">
            {t(k.femaleHormonesMeasureEyebrow)}
          </p>
          <h2 className="type-display-md text-neutral-900">
            {t(k.femaleHormonesMeasureHeading)}
          </h2>
          <p className="type-body-xl max-w-[520px] text-neutral-500">
            {t(k.femaleHormonesMeasureBody)}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {MEASURE_CARDS.map((card) => (
            <MeasureMarkerCard key={card.titleKey} card={card} t={t} />
          ))}
        </div>

        <div className="mt-14 flex flex-col items-center gap-6">
          <BookingCta
            productId={[PRODUCT_IDS.bloodTest, PRODUCT_IDS.hormoneWomen]}
            step="addons"
            trackingLabel="female_hormones_measure_cta"
            trackingLocation="go/female-hormones"
            className="block w-full sm:inline-block sm:w-auto"
          >
            <Button
              size="lg"
              variant="primary"
              className="group w-full sm:w-auto"
              iconRight={
                <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
              }
            >
              {t(k.femaleHormonesHeroCta1)}
            </Button>
          </BookingCta>
          <CtaReassurance />
        </div>
      </div>
    </section>
  )
}

/* ── Stats bar — proof points at a glance, tight under the report ── */
export function FemaleHormonesStats() {
  const t = useTranslations("go")
  return (
    <section className="pt-0 pb-12 md:pb-16">
      <div className={`${SECTION} grid grid-cols-2 gap-y-8 md:grid-cols-4`}>
        {STATS.map((s, i) => (
          <div
            key={s.labelKey}
            className={`px-6 text-center ${i > 0 ? "md:border-l md:border-neutral-200" : ""}`}
          >
            <p className="type-display-md text-aubergine-800">
              {t(s.valueKey)}
            </p>
            <p className="type-body-sm mt-2 text-neutral-500">
              {t(s.labelKey)}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

/* ── Why not just my GP? — ordinary-vs-Ahead comparison (metabolism layout:
   white highlighted "Ahead" card beside a muted "Typical" column). Format and
   process claims only — no new medical claims. ── */
const GP_AHEAD_ROW_KEYS = [
  k.femaleHormonesGpAheadRow1,
  k.femaleHormonesGpAheadRow2,
  k.femaleHormonesGpAheadRow3,
  k.femaleHormonesGpAheadRow4,
  k.femaleHormonesGpAheadRow5,
  k.femaleHormonesGpAheadRow6,
  k.femaleHormonesGpAheadRow7,
] as const

const GP_TYPICAL_ROW_KEYS = [
  k.femaleHormonesGpTypicalRow1,
  k.femaleHormonesGpTypicalRow2,
  k.femaleHormonesGpTypicalRow3,
  k.femaleHormonesGpTypicalRow4,
  k.femaleHormonesGpTypicalRow5,
  k.femaleHormonesGpTypicalRow6,
  k.femaleHormonesGpTypicalRow7,
] as const

export function FemaleHormonesGpCompare({
  showImaging,
}: {
  /** Blood-only markets drop the imaging row (row 7) from BOTH columns, so the
   *  two lists stay row-for-row aligned. */
  showImaging: boolean
}) {
  const t = useTranslations("go")
  const aheadRowKeys = showImaging
    ? GP_AHEAD_ROW_KEYS
    : GP_AHEAD_ROW_KEYS.filter((key) => key !== k.femaleHormonesGpAheadRow7)
  const typicalRowKeys = showImaging
    ? GP_TYPICAL_ROW_KEYS
    : GP_TYPICAL_ROW_KEYS.filter((key) => key !== k.femaleHormonesGpTypicalRow7)
  return (
    <section className="bg-neutral-50 py-16 md:py-24">
      <div className={SECTION}>
        <h2 className="type-display-md mx-auto max-w-[820px] text-center text-neutral-900">
          {t(k.femaleHormonesGpHeading)}
        </h2>
        <p className="type-body-xl mx-auto mt-4 max-w-[560px] text-center text-neutral-500">
          {t(k.femaleHormonesGpBody)}
        </p>

        <div className="mx-auto mt-12 max-w-[1100px] rounded-[32px] bg-neutral-100 p-4 md:p-6">
          <div className="grid items-stretch gap-4 md:grid-cols-2 md:gap-6">
            {/* left — Ahead, highlighted white card */}
            <div className="flex flex-col justify-center rounded-[24px] bg-white px-7 py-10 md:px-10 md:py-12">
              <h3 className="type-title-lg text-neutral-900">
                {t(k.femaleHormonesGpAheadTitle)}
              </h3>
              <ul className="mt-6 flex flex-col gap-4">
                {aheadRowKeys.map((rowKey) => (
                  <li key={rowKey} className="flex items-center gap-3">
                    <CheckCircleIcon size={22} />
                    <span className="type-body-lg text-neutral-900">
                      {t(rowKey)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* right — Typical, centred in a matching invisible-card footprint */}
            <div className="flex flex-col justify-center px-7 py-10 md:px-10 md:py-12">
              <h3 className="type-title-lg text-neutral-700">
                {t(k.femaleHormonesGpTypicalTitle)}
              </h3>
              <ul className="mt-6 flex flex-col gap-4">
                {typicalRowKeys.map((rowKey) => (
                  <li key={rowKey} className="flex items-center gap-3">
                    <span
                      aria-hidden
                      className="h-0.5 w-[22px] shrink-0 rounded-full bg-neutral-300"
                    />
                    <span className="type-body-lg text-neutral-500">
                      {t(rowKey)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── The offer. Two states:
     1. imaging sellable → blood + hormone add-on vs the full check-up with MRI
        (BloodTrackingComparison, blood-mri variant). The MRI card books the
        Advanced-tier check-up (the tier carrying the dedicated contrast-free
        pelvic sequence), never bare Core.
     2. not sellable → the standalone one-off blood card in this section's dark
        shell, carrying the primary CTA. The women's hormone add-on stays priced
        and preselected on it. No membership fork: membership rides the same
        EXPANSION flag as imaging, so a market without one has neither.
   Never a coming-soon/waitlist downgrade: on an ad LP a product the visitor
   can't buy is a conversion leak, not a teaser. ── */
export function FemaleHormonesPricing({
  showImaging,
}: {
  /** Is the imaging arm (full-body + pelvic MRI) sellable in this market? */
  showImaging: boolean
}) {
  const t = useTranslations("go")
  const fullCheckupHref = useFullCheckupHref()
  // Prices come from the live Stripe feed (useLivePrices), not baked into the
  // message strings — the full check-up total is the Advanced (blood+MRI)
  // package, the hormone add-on is the women's hormone panel.
  const { code: country } = useCountry()
  const prices = useLivePrices(country)
  const hormoneAddonPrice = prices.fullPrices.addonHormoneWomen ?? ""

  return (
    <section
      id="pricing"
      className="bg-deep-blue-950 relative scroll-mt-24 overflow-hidden text-white"
    >
      {/* Mountain-range backdrop — slightly scaled + blurred, with a single
          light neutral dark shade over it so the colour photo stays visible
          while the white heading + cards remain legible (metabolism treatment). */}
      <div aria-hidden className="absolute inset-0">
        <Image
          src={blobMediaUrl("/images/metabolism/pricing-mountain-range.webp")}
          alt=""
          fill
          sizes="100vw"
          className="scale-105 object-cover object-center blur-sm"
        />
        <div className="absolute inset-0 bg-neutral-950/60" />
      </div>
      <div className="relative z-10 mx-auto max-w-[1100px] px-6 py-20 md:px-10 md:py-28">
        <div className="mx-auto max-w-[720px] text-center">
          <p className="type-label-md text-white/50 uppercase">
            {t(k.femaleHormonesOfferEyebrow)}
          </p>
          <h2 className="type-display-md mt-4 text-white">
            {t(k.femaleHormonesOfferHeading)}
          </h2>
          <p className="type-body-lg mx-auto mt-5 max-w-[560px] text-white/60">
            {t(k.femaleHormonesOfferBody)}
          </p>
        </div>

        <div className="mt-14">
          {showImaging ? (
            <BloodTrackingComparison
              offer="blood-mri"
              trackingLocation="go/female-hormones"
              baselineCtaLabel={t(k.femaleHormonesHeroCta1)}
              baselineProductId={[
                PRODUCT_IDS.bloodTest,
                PRODUCT_IDS.hormoneWomen,
              ]}
              baselinePriceNote={t(k.femaleHormonesPricingBaselineNote, {
                price: hormoneAddonPrice,
              })}
              extraBaselineRows={[
                t(k.femaleHormonesPricingAddonRow, {
                  price: hormoneAddonPrice,
                }),
              ]}
              mriHref={fullCheckupHref}
              mriPriceFrom={t(k.femaleHormonesPricingMriPriceFrom)}
              mriPrice={t(k.femaleHormonesPricingMriPrice, {
                price: prices.fullPrices.advanced ?? "",
              })}
              mriPriceNote={t(k.femaleHormonesPricingBaselineNote, {
                price: hormoneAddonPrice,
              })}
              mriCardTagline={t(k.femaleHormonesPricingMriCardTagline)}
              mriRows={[
                t(k.femaleHormonesPricingMriRow1),
                t(k.femaleHormonesPricingMriRow2),
                t(k.femaleHormonesPricingMriRow3),
              ]}
            />
          ) : (
            /* State 3 — blood-only market: the standalone one-off card, the
               same single-card path NL/DE already render when membership is
               off. The women's hormone add-on stays priced (baselinePriceNote
               + extraBaselineRows) and preselected (baselineProductId), so the
               card still sells this page's actual offer. */
            <BloodTrackingComparison
              showMembership={false}
              trackingLocation="go/female-hormones"
              baselineCtaLabel={t(k.femaleHormonesHeroCta1)}
              baselineProductId={[
                PRODUCT_IDS.bloodTest,
                PRODUCT_IDS.hormoneWomen,
              ]}
              baselinePriceNote={t(k.femaleHormonesPricingBaselineNote, {
                price: hormoneAddonPrice,
              })}
              /* The add-on row alone makes a thin card; the two facts under it
                 are already translated here and hold for the one-off test. */
              extraBaselineRows={[
                t(k.femaleHormonesPricingAddonRow, {
                  price: hormoneAddonPrice,
                }),
                t(k.femaleHormonesHeroKpi3Value),
                t(k.femaleHormonesHeroKpi3Label),
              ]}
            />
          )}
        </div>

        <p className="mx-auto mt-10 max-w-[680px] text-center text-base leading-relaxed font-medium text-white/70">
          {t(k.femaleHormonesOfferFootnote)}
        </p>
      </div>
    </section>
  )
}

/* ── Closing CTA — confident, plain close over navy, booking the blood test
   with the women's hormone add-on preselected (same destination as the hero). ── */
export function FemaleHormonesClosingCta() {
  const t = useTranslations("go")
  return (
    <section className="relative isolate flex min-h-[620px] items-center overflow-hidden text-white md:min-h-[720px]">
      <Image
        src={blobMediaUrl("/images/marketing/closing-cta-female-hormones.jpg")}
        alt=""
        aria-hidden
        fill
        sizes="100vw"
        className="object-cover"
      />
      {/* dark scrim keeps the white copy legible over the photo */}
      <div aria-hidden className="absolute inset-0 bg-neutral-950/70" />
      <div
        className={`${SECTION} relative z-10 w-full py-20 text-center md:py-28`}
      >
        <p className="type-label-md text-white/60 uppercase">
          {t(k.femaleHormonesHeroEyebrow)}
        </p>
        <h2 className="type-display-md mx-auto mt-5 max-w-[680px] text-white">
          {t(k.femaleHormonesCtaHeading)}
        </h2>
        <p className="type-body-lg mx-auto mt-5 max-w-[520px] text-white/70">
          {t(k.femaleHormonesCtaBody)}
        </p>
        <div className="mt-10 flex flex-col items-center gap-6">
          <BookingCta
            productId={[PRODUCT_IDS.bloodTest, PRODUCT_IDS.hormoneWomen]}
            step="addons"
            trackingLabel="female_hormones_bottom_cta"
            trackingLocation="go/female-hormones"
            className="block w-full sm:inline-block sm:w-auto"
          >
            <Button
              size="lg"
              variant="primary"
              className="group w-full sm:w-auto"
              iconRight={
                <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
              }
            >
              {t(k.femaleHormonesHeroCta1)}
            </Button>
          </BookingCta>
          <CtaReassurance tone="onDark" />
        </div>
      </div>
    </section>
  )
}
